import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { LogOut, Edit, Trash2, Image as ImageIcon, Save } from 'lucide-react';
import ImageEditor from '../components/ImageEditor';

const AdminGallery = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editedProject, setEditedProject] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    // Check auth
    if (!localStorage.getItem('adminAuth')) {
      navigate('/admin/login');
      return;
    }
    fetchProjects();
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects?limit=500`);
      setProjects(response.data.projects || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin/login');
  };

  const handleEditProject = (project) => {
    setSelectedProject(project);
    setEditedProject({...project});
  };

  const handleSaveProject = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/projects/${editedProject.id}`, editedProject);
      await fetchProjects();
      setSelectedProject(null);
      setEditedProject(null);
      alert('Projet mis à jour avec succès!');
    } catch (error) {
      console.error('Error updating project:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce projet ?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}`);
      await fetchProjects();
      alert('Projet supprimé avec succès!');
    } catch (error) {
      console.error('Error deleting project:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const categories = ['Béton Imprimé', 'Béton Désactivé', 'Béton Bâlé'];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white py-4 px-6">
        <div className="container mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">Admin Panel - Galerie</h1>
            <p className="text-slate-300 text-sm">{projects.length} projets au total</p>
          </div>
          <div className="flex gap-2">
            <Link to="/admin">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                🏠 Dashboard
              </Button>
            </Link>
            <Link to="/admin/crm">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                📊 CRM
              </Button>
            </Link>
            <Link to="/admin/groups">
              <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                📁 Groupes
              </Button>
            </Link>
            <Button onClick={handleLogout} variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto p-6">
        {loading ? (
          <div className="text-center py-12">
            <p className="text-xl text-slate-600">Chargement...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {projects.map((project) => (
              <Card key={project.id} className="overflow-hidden group relative">
                <div className="relative h-64 bg-slate-200">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.src = 'https://via.placeholder.com/400x300?text=Image'}
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button 
                          size="sm" 
                          className="bg-white text-slate-900 hover:bg-slate-100"
                          onClick={() => handleEditProject(project)}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Modifier
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                          <DialogTitle>Modifier le Projet</DialogTitle>
                        </DialogHeader>
                        {editedProject && (
                          <Tabs defaultValue="info" className="w-full">
                            <TabsList className="grid w-full grid-cols-2">
                              <TabsTrigger value="info">Informations</TabsTrigger>
                              <TabsTrigger value="image">Modifier Image</TabsTrigger>
                            </TabsList>
                            
                            <TabsContent value="info" className="space-y-4 mt-4">
                              <div className="space-y-2">
                                <Label>Titre</Label>
                                <Input 
                                  value={editedProject.title}
                                  onChange={(e) => setEditedProject({...editedProject, title: e.target.value})}
                                />
                              </div>
                              
                              <div className="space-y-2">
                                <Label>Catégorie</Label>
                                <Select 
                                  value={editedProject.category}
                                  onValueChange={(value) => setEditedProject({...editedProject, category: value})}
                                >
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {categories.map((cat) => (
                                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label>Localisation</Label>
                                  <Input 
                                    value={editedProject.location}
                                    onChange={(e) => setEditedProject({...editedProject, location: e.target.value})}
                                  />
                                </div>
                                <div className="space-y-2">
                                  <Label>Surface (m²)</Label>
                                  <Input 
                                    value={editedProject.surface}
                                    onChange={(e) => setEditedProject({...editedProject, surface: e.target.value})}
                                  />
                                </div>
                              </div>

                              <div className="space-y-2">
                                <Label>Description</Label>
                                <Input 
                                  value={editedProject.description}
                                  onChange={(e) => setEditedProject({...editedProject, description: e.target.value})}
                                />
                              </div>

                              <Button onClick={handleSaveProject} className="w-full">
                                <Save className="w-4 h-4 mr-2" />
                                Sauvegarder
                              </Button>
                            </TabsContent>

                            <TabsContent value="image" className="mt-4">
                              <ImageEditor 
                                project={editedProject}
                                onSave={(updatedProject) => {
                                  setEditedProject(updatedProject);
                                  handleSaveProject();
                                }}
                              />
                            </TabsContent>
                          </Tabs>
                        )}
                      </DialogContent>
                    </Dialog>
                    
                    <Button 
                      size="sm" 
                      variant="destructive"
                      onClick={() => handleDeleteProject(project.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminGallery;